import _objectSpread from "/usr/app/node_modules/@babel/runtime/helpers/esm/objectSpread2.js";
import _regenerator from "/usr/app/node_modules/@babel/runtime/helpers/esm/regenerator.js";
import _asyncToGenerator from "/usr/app/node_modules/@babel/runtime/helpers/esm/asyncToGenerator.js";
import _slicedToArray from "/usr/app/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import React from "react";
var __jsx = React.createElement;
import { useTranslation } from "@i18n";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useState, useEffect, useMemo, useRef } from "react";
import InfoModal from "@/pages/checkout/dhamen/components/common/InfoModal";
import Input from "@/components/UiKit/Input/Input";
import InputPhoneNumber from "@componentsShared/InputPhoneNumber/InputPhoneNumber";
import DateCalendarGregorian from "@componentsShared/DateCalendarGregorian/DateCalendarGregorian";
import { gregorianToHijri } from "@componentsShared/DateCalendarGregorian/DateCalendarHelpers";
import InputCurrency from "@componentsShared/InputCurrency/InputCurrency";
import HeadingSteps from "@/pages/checkout/shop-apply/shopApplyForm/HeadingSteps/HeadingSteps";
import useIsMobile from "@hooks/useIsMobile";
import { twMerge } from "tailwind-merge";
import MobileDesktopButton from "@/components/shared/MobileDesktopButton/MobileDesktopButton";
import OrderData from "@/pages/checkout/dhamen/components/DhamenFormScreen/OrderData";
import { useDhamenContext } from "@/common/Contexts/DhamenContextProvider";
import { api__validateUserData, api__submitDhamenFormData } from "@/pages/checkout/dhamen/api";
import { toast } from "sonner";
import { handleGtmEvent } from "@/common/events/gtmEvents";
import { getPlatformInfo } from "@/utils/GlobalJavascriptFunction";
import { DHAMEN_STATUS, MIN_FINANCING_AMOUNT, MIN_MONTHLY_INCOME } from "@/pages/checkout/dhamen/constants";
// Zod schema for form validation
var formSchema = z.object({
  first_name: z.string().min(1, "dhamen/error_first_name_required").min(2, "dhamen/error_first_name_min_length").regex(/^[\u0600-\u06FFa-zA-Z\s]+$/, "dhamen/error_first_name_invalid_chars"),
  last_name: z.string().min(1, "dhamen/error_last_name_required").min(2, "dhamen/error_last_name_min_length").regex(/^[\u0600-\u06FFa-zA-Z\s]+$/, "dhamen/error_last_name_invalid_chars"),
  id_number: z.string().min(1, "dhamen/error_id_number_required").regex(/^[12]\d{9}$/, "dhamen/error_id_number_invalid_format"),
  mobile_number: z.string().min(1, "dhamen/error_mobile_number_required").regex(/^05\d{8}$/, "dhamen/error_mobile_number_invalid_format"),
  birth_date: z.string().min(1, "dhamen/error_birth_date_required"),
  birth_date_h: z.string().optional(),
  birth_date_type: z["enum"](["G", "H"])["default"]("G"),
  monthly_income: z.string().min(1, "dhamen/error_monthly_income_required").refine(function (val) {
    return !isNaN(Number(val)) && Number(val) >= MIN_MONTHLY_INCOME;
  }, {
    message: "dhamen/error_monthly_income_min_value"
  }),
  monthly_obligations: z.string().optional().refine(function (val) {
    return val === undefined || val === "" || !isNaN(Number(val)) && Number(val) >= 0;
  }, {
    message: "dhamen/error_monthly_obligations_min_value"
  }),
  has_down_payment: z["boolean"]().optional(),
  down_payment: z.string().optional(),
  financing_duration: z.string().min(1, "dhamen/error_financing_duration_required").refine(function (val) {
    return ["1", "2", "3", "4", "5"].includes(val);
  }, {
    message: "dhamen/error_financing_duration_invalid"
  }),
  consent: z["boolean"]().refine(function (val) {
    return val === true;
  }, {
    message: "dhamen/error_consent_required"
  })
}).superRefine(function (data, ctx) {
  if (data.has_down_payment) {
    if (!data.down_payment || data.down_payment.trim() === "") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "dhamen/error_down_payment_required",
        path: ["down_payment"]
      });
    } else if (isNaN(Number(data.down_payment)) || Number(data.down_payment) <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "dhamen/error_down_payment_min_value",
        path: ["down_payment"]
      });
    }
  }
});
function DhamenInfoForm() {
  var _state$orderInfo, _state$orderInfo$orde, _state$orderInfo2;
  var _useTranslation = useTranslation(["web_checkout_finance"]),
    t = _useTranslation.t,
    tShort = _useTranslation.tShort;
  var _useState = useState(false),
    isValidating = _useState[0],
    setIsValidating = _useState[1];
  var _useState2 = useState(false),
    showTermsModal = _useState2[0],
    setShowTermsModal = _useState2[1];
  var _useDhamenContext = useDhamenContext(),
    state = _useDhamenContext.state,
    actions = _useDhamenContext.actions;
  // Use activeTab from context instead of local state
  var activeTab = state.activeTab;
  // Track if form has been initialized to prevent infinite loops
  var hasInitialized = useRef(false);
  // console.log("state", state);
  // Extract customer data from context for default values
  var customerData = (_state$orderInfo = state.orderInfo) === null || _state$orderInfo === void 0 ? void 0 : (_state$orderInfo$orde = _state$orderInfo.order) === null || _state$orderInfo$orde === void 0 ? void 0 : _state$orderInfo$orde.customer;

  // Helper function to normalize birth date to YYYY-MM-DD format (API format)
  var normalizeBirthDate = function normalizeBirthDate(dateStr) {
    if (!dateStr) return "";

    // If already in YYYY-MM-DD format, return as is
    if (dateStr.includes("-")) {
      return dateStr;
    }

    // Support legacy DD/MM/YYYY format by converting to YYYY-MM-DD
    if (dateStr.includes("/")) {
      var parts = dateStr.split("/");
      if (parts.length === 3) {
        var _parts = _slicedToArray(parts, 3),
          day = _parts[0],
          month = _parts[1],
          year = _parts[2];
        var paddedDay = String(day).padStart(2, "0");
        var paddedMonth = String(month).padStart(2, "0");
        return "".concat(year, "-").concat(paddedMonth, "-").concat(paddedDay);
      }
    }
    return dateStr;
  };

  // Extract financing details from context for default values
  var financingDetails = (_state$orderInfo2 = state.orderInfo) === null || _state$orderInfo2 === void 0 ? void 0 : _state$orderInfo2.financing_details;

  // Create default values from context data using useMemo
  var contextDefaultValues = useMemo(function () {
    // Parse the birth date and calculate Hijri equivalent if available
    var gregorianBirthDate = normalizeBirthDate((customerData === null || customerData === void 0 ? void 0 : customerData.birth_date) || "");
    var hijriBirthDate = "";
    if (gregorianBirthDate) {
      var _gregorianBirthDate$s = gregorianBirthDate.split("-").map(Number),
        _gregorianBirthDate$s2 = _slicedToArray(_gregorianBirthDate$s, 3),
        year = _gregorianBirthDate$s2[0],
        month = _gregorianBirthDate$s2[1],
        day = _gregorianBirthDate$s2[2];
      var gregorianDate = new Date(year, month - 1, day);
      var hijriDate = gregorianToHijri(gregorianDate);
      if (hijriDate) {
        // hijriDate.month is now 0-based (after AdhanService fix)
        hijriBirthDate = "".concat(hijriDate.year, "-").concat(String(hijriDate.month + 1).padStart(2, "0"), "-").concat(String(hijriDate.day).padStart(2, "0"));
      }
    }
    return {
      first_name: (customerData === null || customerData === void 0 ? void 0 : customerData.first_name) || "",
      last_name: (customerData === null || customerData === void 0 ? void 0 : customerData.last_name) || "",
      id_number: (customerData === null || customerData === void 0 ? void 0 : customerData.id_number) || "",
      mobile_number: (customerData === null || customerData === void 0 ? void 0 : customerData.phone_number) || "",
      // Always keep birth_date in form state as YYYY-MM-DD (Gregorian)
      birth_date: gregorianBirthDate,
      birth_date_h: hijriBirthDate,
      birth_date_type: "G",
      monthly_income: financingDetails !== null && financingDetails !== void 0 && financingDetails.requested_monthly_income ? String(financingDetails.requested_monthly_income) : "",
      monthly_obligations: financingDetails !== null && financingDetails !== void 0 && financingDetails.requested_monthly_commitments ? String(financingDetails.requested_monthly_commitments) : "",
      has_down_payment: financingDetails !== null && financingDetails !== void 0 && financingDetails.requested_first_payment ? financingDetails.requested_first_payment > 0 : false,
      down_payment: financingDetails !== null && financingDetails !== void 0 && financingDetails.requested_first_payment ? String(financingDetails.requested_first_payment) : "",
      financing_duration: financingDetails !== null && financingDetails !== void 0 && financingDetails.contract_term_years ? String(financingDetails.contract_term_years) : "3",
      consent: state.consent
    };
  }, [customerData, financingDetails, state.consent]);

  // Handle second step submission
  var handleFinanceFormSubmit = /*#__PURE__*/function () {
    var _ref = _asyncToGenerator(/*#__PURE__*/_regenerator().m(function _callee(data) {
      var _state$orderInfo3, _state$orderInfo3$ord, _state$orderInfo4, _state$orderInfo4$ord, _state$orderInfo5, _state$orderInfo5$ord, _state$orderInfo6, _state$orderInfo6$ord, _state$orderInfo6$ord2, submitData, response, _state$orderInfo7, _state$orderInfo7$ord, _state$orderInfo8, _state$orderInfo8$ord, _state$orderInfo9, _state$orderInfo9$ord, _state$orderInfo0, _state$orderInfo0$ord, _state$orderInfo0$ord2, _state$orderInfo1, _state$orderInfo1$fin, _state$orderInfo10, _state$orderInfo10$fi, _state$orderInfo11, _state$orderInfo11$fi, _response$data, errorMessage, fieldErrors, _t;
      return _regenerator().w(function (_context) {
        while (1) switch (_context.p = _context.n) {
          case 0:
            _context.p = 0;
            setIsValidating(true);

            // Update context with form values before API call
            actions.updateCustomerData({
              first_name: data.first_name,
              last_name: data.last_name,
              id_number: data.id_number,
              phone_number: data.mobile_number,
              birth_date: data.birth_date
            });
            actions.updateRequestedFinancingData({
              requested_monthly_income: parseFloat(data.monthly_income),
              requested_monthly_commitments: parseFloat(data.monthly_obligations) || 0,
              requested_first_payment: data.has_down_payment ? parseFloat(data.down_payment || "0") : 0,
              requested_financing_amount: state.financingAmount,
              contract_term_years: parseInt(data.financing_duration),
              contract_term: parseInt(data.financing_duration) * 12
            });

            // Prepare data for API (send both Gregorian and Hijri dates)
            submitData = {
              first_name: data.first_name,
              last_name: data.last_name,
              id_number: data.id_number,
              uuid: state.orderInfo.order.order_uuid,
              date_of_birth: data.birth_date,
              date_of_birth_h: data.birth_date_h || "",
              date_of_birth_type: data.birth_date_type,
              monthly_income: parseFloat(data.monthly_income),
              period: parseInt(data.financing_duration) * 12,
              // Convert years to months
              first_payment: data.has_down_payment ? parseFloat(data.down_payment || "0") : 0,
              monthly_commitments: parseFloat(data.monthly_obligations),
              requested_financing_amount: state.financingAmount
            }; // Track form submission event
            handleGtmEvent("dhamen_form_submit", {
              order_id: state === null || state === void 0 ? void 0 : (_state$orderInfo3 = state.orderInfo) === null || _state$orderInfo3 === void 0 ? void 0 : (_state$orderInfo3$ord = _state$orderInfo3.order) === null || _state$orderInfo3$ord === void 0 ? void 0 : _state$orderInfo3$ord.id,
              post_id: state === null || state === void 0 ? void 0 : (_state$orderInfo4 = state.orderInfo) === null || _state$orderInfo4 === void 0 ? void 0 : (_state$orderInfo4$ord = _state$orderInfo4.order) === null || _state$orderInfo4$ord === void 0 ? void 0 : _state$orderInfo4$ord.post_id,
              transaction_id: state === null || state === void 0 ? void 0 : (_state$orderInfo5 = state.orderInfo) === null || _state$orderInfo5 === void 0 ? void 0 : (_state$orderInfo5$ord = _state$orderInfo5.order) === null || _state$orderInfo5$ord === void 0 ? void 0 : _state$orderInfo5$ord.order_uuid,
              post_price: state === null || state === void 0 ? void 0 : (_state$orderInfo6 = state.orderInfo) === null || _state$orderInfo6 === void 0 ? void 0 : (_state$orderInfo6$ord = _state$orderInfo6.order) === null || _state$orderInfo6$ord === void 0 ? void 0 : (_state$orderInfo6$ord2 = _state$orderInfo6$ord.pricing) === null || _state$orderInfo6$ord2 === void 0 ? void 0 : _state$orderInfo6$ord2.total_price,
              platform: getPlatformInfo(),
              salary: parseFloat(data.monthly_income),
              monthly_commitments: parseFloat(data.monthly_obligations),
              financing_duration: parseInt(data.financing_duration),
              first_payment: data.has_down_payment ? parseFloat(data.down_payment || "0") : 0,
              requested_financing_amount: state.financingAmount
            });
            // Call the API
            _context.n = 1;
            return api__submitDhamenFormData(submitData);
          case 1:
            response = _context.v;
            if (response.success && response.data) {
              // Update context with the financing offer data
              actions.updateFinancingDetails({
                financing_amount: response.data.financing_amount_formatted,
                monthly_installment: response.data.monthly_installment_formatted,
                apr: response.data.apr,
                contract_term: response.data.contract_term,
                contract_term_years: response.data.contract_term_years,
                admin_fees: response.data.admin_fees_formatted,
                offer_expiry_date: response.data.offer_expiry_date
              });

              // Track offer received event
              handleGtmEvent("dhamen_offer_receive", {
                order_id: state === null || state === void 0 ? void 0 : (_state$orderInfo7 = state.orderInfo) === null || _state$orderInfo7 === void 0 ? void 0 : (_state$orderInfo7$ord = _state$orderInfo7.order) === null || _state$orderInfo7$ord === void 0 ? void 0 : _state$orderInfo7$ord.id,
                post_id: state === null || state === void 0 ? void 0 : (_state$orderInfo8 = state.orderInfo) === null || _state$orderInfo8 === void 0 ? void 0 : (_state$orderInfo8$ord = _state$orderInfo8.order) === null || _state$orderInfo8$ord === void 0 ? void 0 : _state$orderInfo8$ord.post_id,
                transaction_id: state === null || state === void 0 ? void 0 : (_state$orderInfo9 = state.orderInfo) === null || _state$orderInfo9 === void 0 ? void 0 : (_state$orderInfo9$ord = _state$orderInfo9.order) === null || _state$orderInfo9$ord === void 0 ? void 0 : _state$orderInfo9$ord.order_uuid,
                post_price: state === null || state === void 0 ? void 0 : (_state$orderInfo0 = state.orderInfo) === null || _state$orderInfo0 === void 0 ? void 0 : (_state$orderInfo0$ord = _state$orderInfo0.order) === null || _state$orderInfo0$ord === void 0 ? void 0 : (_state$orderInfo0$ord2 = _state$orderInfo0$ord.pricing) === null || _state$orderInfo0$ord2 === void 0 ? void 0 : _state$orderInfo0$ord2.total_price,
                platform: getPlatformInfo(),
                salary: (_state$orderInfo1 = state.orderInfo) === null || _state$orderInfo1 === void 0 ? void 0 : (_state$orderInfo1$fin = _state$orderInfo1.financing_details) === null || _state$orderInfo1$fin === void 0 ? void 0 : _state$orderInfo1$fin.requested_monthly_income,
                // todo
                monthly_commitments: (_state$orderInfo10 = state.orderInfo) === null || _state$orderInfo10 === void 0 ? void 0 : (_state$orderInfo10$fi = _state$orderInfo10.financing_details) === null || _state$orderInfo10$fi === void 0 ? void 0 : _state$orderInfo10$fi.requested_monthly_commitments,
                first_payment: (_state$orderInfo11 = state.orderInfo) === null || _state$orderInfo11 === void 0 ? void 0 : (_state$orderInfo11$fi = _state$orderInfo11.financing_details) === null || _state$orderInfo11$fi === void 0 ? void 0 : _state$orderInfo11$fi.requested_first_payment,
                financing_duration: state.orderInfo.financing_details.contract_term_years,
                requested_financing_amount: state.orderInfo.financing_details.requested_financing_amount,
                approved_financing_amount: state.orderInfo.financing_details.financing_amount,
                fees_vat: state.orderInfo.financing_details.admin_fees,
                APR: state.orderInfo.financing_details.apr,
                monthly_installment: state.orderInfo.financing_details.monthly_installment
              });

              // Show success toast
              toast.success(tShort("dhamen/finance_submit_success"), {
                position: "bottom-center",
                duration: 3000
              });

              // Call the original onSubmit to proceed to next screen
              actions.setStatus(DHAMEN_STATUS.SUBMITTED);
            } else {
              // Handle API error
              errorMessage = tShort("dhamen/finance_submit_error"); // Handle specific field errors from API (code 230)
              if (response !== null && response !== void 0 && response.errors && typeof response.errors === 'object') {
                // Set form errors for specific fields
                Object.keys(response.errors).forEach(function (fieldName) {
                  var errorMsg = response.errors[fieldName];

                  // Map API field names to form field names and set errors
                  if (fieldName === "requested_financing_amount") {
                    // This error is about the overall financing amount, not a specific form field
                    // We'll show it in the toast
                  } else if (fieldName === "monthly_income") {
                    setError("monthly_income", {
                      type: "manual",
                      message: errorMsg
                    });
                  } else if (fieldName === "monthly_obligations") {
                    setError("monthly_obligations", {
                      type: "manual",
                      message: errorMsg
                    });
                  } else if (fieldName === "down_payment" || fieldName === "first_payment") {
                    setError("down_payment", {
                      type: "manual",
                      message: errorMsg
                    });
                  } else if (fieldName === "financing_duration" || fieldName === "period") {
                    setError("financing_duration", {
                      type: "manual",
                      message: errorMsg
                    });
                  }
                });

                // Extract all error messages for the toast
                fieldErrors = Object.values(response.errors);
                errorMessage = fieldErrors.join(", ");
              } else if (response !== null && response !== void 0 && (_response$data = response.data) !== null && _response$data !== void 0 && _response$data.message) {
                // Handle other API error formats
                errorMessage = response.data.message;
              } else if (response !== null && response !== void 0 && response.error) {
                // Handle general error field
                errorMessage = response.error;
              }
              toast.error(errorMessage, {
                position: "bottom-center",
                duration: 5000
              });
            }
            _context.n = 3;
            break;
          case 2:
            _context.p = 2;
            _t = _context.v;
            console.error("Error submitting financing data:", _t);
            toast.error(tShort("dhamen/connection_error"), {
              position: "bottom-center",
              duration: 5000
            });
          case 3:
            _context.p = 3;
            setIsValidating(false);
            return _context.f(3);
          case 4:
            return _context.a(2);
        }
      }, _callee, null, [[0, 2, 3, 4]]);
    }));
    return function handleFinanceFormSubmit(_x) {
      return _ref.apply(this, arguments);
    };
  }();
  var _useForm = useForm({
      resolver: zodResolver(formSchema),
      mode: "onTouched",
      defaultValues: _objectSpread({}, contextDefaultValues)
    }),
    control = _useForm.control,
    handleSubmit = _useForm.handleSubmit,
    _useForm$formState = _useForm.formState,
    errors = _useForm$formState.errors,
    isValid = _useForm$formState.isValid,
    setValue = _useForm.setValue,
    watch = _useForm.watch,
    reset = _useForm.reset,
    setError = _useForm.setError,
    clearErrors = _useForm.clearErrors;

  // Update form values when context data becomes available (only once on initial load)
  useEffect(function () {
    if (customerData && !state.loading && !hasInitialized.current) {
      var newDefaultValues = _objectSpread({}, contextDefaultValues);
      reset(newDefaultValues);
      hasInitialized.current = true;
    }
  }, [state.loading, state.orderInfo]);
  var birthDateValue = watch("birth_date");
  var downPaymentValue = watch("down_payment");
  var hasDownPayment = watch("has_down_payment");
  var monthlyIncomeValue = watch("monthly_income");
  var monthlyObligationsValue = watch("monthly_obligations");
  var consentValue = watch("consent");
  var idNumberValue = watch("id_number");

  // Watch for down payment changes and update financing amount
  useEffect(function () {
    // Only update if we have orderInfo loaded
    if (!state.orderInfo || state.loading) return;
    if (hasDownPayment && downPaymentValue) {
      var downPayment = parseFloat(downPaymentValue) || 0;
      var newFinancingAmount = actions.calculateFinancingAmount(downPayment);
      // Ensure financing amount never goes negative
      var safeFinancingAmount = Math.max(0, newFinancingAmount);

      // Only update if the value actually changed
      if (state.financingAmount !== safeFinancingAmount) {
        actions.setFinancingAmount(safeFinancingAmount);
      }
    } else if (!hasDownPayment) {
      // Reset to original financing amount when no down payment
      var originalAmount = parseFloat(state.orderInfo.order.pricing.remaining_price.replace(/,/g, ""));

      // Only update if the value actually changed
      if (state.financingAmount !== originalAmount) {
        actions.setFinancingAmount(originalAmount);
      }
    }
  }, [downPaymentValue, hasDownPayment]);

  // Watch for monthly obligations and income changes to validate 45% rule
  useEffect(function () {
    var monthlyIncome = Number(monthlyIncomeValue) || 0;
    var currentObligations = Number(monthlyObligationsValue) || 0;
    var maxAllowedObligations = monthlyIncome * 0.45;
    if (monthlyIncome > 0 && currentObligations > 0 && currentObligations >= maxAllowedObligations) {
      setError("monthly_obligations", {
        type: "manual",
        message: tShort("dhamen/error_monthly_obligations_max_value")
      });
    } else if (currentObligations <= maxAllowedObligations) {
      // Clear error if validation passes
      clearErrors("monthly_obligations");
    }
  }, [monthlyIncomeValue, monthlyObligationsValue]);

  // Check if financing amount exceeds limit based on ID number
  useEffect(function () {
    if (idNumberValue && state.financingAmount) {
      actions.checkExceedLimit(idNumberValue, state.financingAmount);
    }
  }, [idNumberValue, state.financingAmount]);

  // Calculate maximum allowed down payment
  var getMaxDownPayment = function getMaxDownPayment() {
    var _state$orderInfo12, _state$orderInfo12$or;
    if (!((_state$orderInfo12 = state.orderInfo) !== null && _state$orderInfo12 !== void 0 && (_state$orderInfo12$or = _state$orderInfo12.order) !== null && _state$orderInfo12$or !== void 0 && _state$orderInfo12$or.pricing)) return 0;
    var pricing = state.orderInfo.order.pricing;
    var totalPrice = parseFloat(pricing.total_price.replace(/,/g, ""));
    var arboon = parseFloat(pricing.arboon || "0");

    // Maximum down payment = total_price - arboon - MIN_FINANCING_AMOUNT
    return Math.max(0, totalPrice - arboon - MIN_FINANCING_AMOUNT);
  };

  // Custom validation for down payment
  var validateDownPayment = function validateDownPayment(downPayment) {
    if (!hasDownPayment || !downPayment || !state.orderInfo) return null;
    var downPaymentAmount = parseFloat(downPayment) || 0;
    var maxAllowed = getMaxDownPayment();
    var calculatedFinancingAmount = actions.calculateFinancingAmount(downPaymentAmount);

    // Prevent negative financing amount
    if (calculatedFinancingAmount < 0 || downPaymentAmount > maxAllowed) {
      return "".concat(tShort("dhamen/error_down_payment_max_value"), " ").concat(maxAllowed.toLocaleString(), "  SAR");
    }
    if (calculatedFinancingAmount < MIN_FINANCING_AMOUNT) {
      return "".concat(tShort("dhamen/error_financing_amount_min_value"), " ").concat(MIN_FINANCING_AMOUNT.toLocaleString(), " SAR");
    }
    return null;
  };
  var parseBirthDate = function parseBirthDate(dateStr) {
    var calendarType = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : "G";
    if (!dateStr) return null;

    // Preferred format: YYYY-MM-DD
    if (dateStr.includes("-")) {
      var parts = dateStr.split("-");
      if (parts.length === 3) {
        var _parts2 = _slicedToArray(parts, 3),
          year = _parts2[0],
          month = _parts2[1],
          day = _parts2[2];
        return {
          year: parseInt(year),
          month: parseInt(month) - 1,
          day: parseInt(day),
          type: calendarType
        };
      }
    }

    // Legacy support: DD/MM/YYYY
    if (dateStr.includes("/")) {
      var _parts3 = dateStr.split("/");
      if (_parts3.length === 3) {
        var _parts4 = _slicedToArray(_parts3, 3),
          _day = _parts4[0],
          _month = _parts4[1],
          _year = _parts4[2];
        return {
          year: parseInt(_year),
          month: parseInt(_month) - 1,
          day: parseInt(_day),
          type: calendarType
        };
      }
    }
    return null;
  };
  var handleNextTab = /*#__PURE__*/function () {
    var _ref2 = _asyncToGenerator(/*#__PURE__*/_regenerator().m(function _callee2() {
      var formValues, validationResult, _validationResult$dat, errorMessage, fieldErrors, networkErrorMessage, _t2;
      return _regenerator().w(function (_context2) {
        while (1) switch (_context2.p = _context2.n) {
          case 0:
            _context2.p = 0;
            setIsValidating(true);

            // Get current form values
            formValues = watch(); // Call validation API with step 1 data (send both Gregorian and Hijri dates)
            _context2.n = 1;
            return api__validateUserData({
              id_number: formValues.id_number,
              date_of_birth: formValues.birth_date,
              date_of_birth_h: formValues.birth_date_h || "",
              date_of_birth_type: formValues.birth_date_type,
              first_name: formValues.first_name,
              last_name: formValues.last_name
            });
          case 1:
            validationResult = _context2.v;
            // If validation is successful, proceed to next tab
            if (validationResult !== null && validationResult !== void 0 && validationResult.success) {
              actions.setActiveTab("finance-form");
            } else {
              // Handle validation error
              console.error("Validation failed:", validationResult);

              // Handle specific field errors
              if (validationResult !== null && validationResult !== void 0 && validationResult.errors) {
                // Set form errors for specific fields
                Object.keys(validationResult.errors).forEach(function (fieldName) {
                  if (fieldName === "date_of_birth") {
                    setError("birth_date", {
                      type: "manual",
                      message: validationResult.errors[fieldName]
                    });
                  }
                  // Handle other field errors if needed
                  if (fieldName === "id_number") {
                    setError("id_number", {
                      type: "manual",
                      message: validationResult.errors[fieldName]
                    });
                  }
                });
              }

              // Show toast error message
              errorMessage = tShort("dhamen/validation_generic_error"); // Handle different error response structures
              if (validationResult !== null && validationResult !== void 0 && (_validationResult$dat = validationResult.data) !== null && _validationResult$dat !== void 0 && _validationResult$dat.message) {
                // Handle 404 and other API errors
                errorMessage = validationResult.data.message;
              } else if (validationResult !== null && validationResult !== void 0 && validationResult.errors && Object.keys(validationResult.errors).length > 0) {
                // Handle validation errors (code 230)
                fieldErrors = Object.values(validationResult.errors);
                errorMessage = fieldErrors.join(", ");
              } else if (validationResult !== null && validationResult !== void 0 && validationResult.error) {
                // Handle general error field
                errorMessage = validationResult.error;
              }

              // Show error toast
              toast.error(errorMessage, {
                position: "bottom-center",
                duration: 5000
              });
              console.error("Validation error:", errorMessage);
            }
            _context2.n = 3;
            break;
          case 2:
            _context2.p = 2;
            _t2 = _context2.v;
            console.error("Error validating user data:", _t2);

            // Show network/API error toast
            networkErrorMessage = (_t2 === null || _t2 === void 0 ? void 0 : _t2.message) || tShort("dhamen/connection_error_with_internet");
            toast.error(networkErrorMessage, {
              position: "bottom-center",
              duration: 5000
            });
          case 3:
            _context2.p = 3;
            setIsValidating(false);
            return _context2.f(3);
          case 4:
            return _context2.a(2);
        }
      }, _callee2, null, [[0, 2, 3, 4]]);
    }));
    return function handleNextTab() {
      return _ref2.apply(this, arguments);
    };
  }();

  // Check if first tab fields are valid
  var isFirstTabValid = function isFirstTabValid() {
    var firstName = watch("first_name");
    var lastName = watch("last_name");
    var idNumber = watch("id_number");
    var birthDate = watch("birth_date");
    var mobileNumber = watch("mobile_number");
    var hasValidationErrors = Object.keys(errors).some(function (key) {
      return ['first_name', 'last_name', 'id_number', 'birth_date', 'mobile_number'].includes(key) && errors[key];
    });
    return firstName && firstName.trim() !== "" && lastName && lastName.trim() !== "" && idNumber && idNumber.trim() !== "" && birthDate && birthDate.trim() !== "" && mobileNumber && mobileNumber.trim() !== "" && !hasValidationErrors;
  };
  var isMobile = useIsMobile();
  return /*#__PURE__*/React.createElement("div", {
    className: twMerge("w-full relative sm:bg-white sm:min-w-[660px] sm:max-w-[660px] md:mx-auto", !isMobile && "border-none sm:rounded-xl sm:border sm:border-solid sm:border-[#EBF0F7] py-4 px-6")
  }, /*#__PURE__*/React.createElement(HeadingSteps, {
    className: isMobile && "mt-4"
  }, /*#__PURE__*/React.createElement("span", {
    style: {
      fontSize: "14px",
      paddingBottom: "4px"
    },
    className: activeTab === "user-info-form" ? "active" : "completed"
  }, tShort("dhamen/user_info")), /*#__PURE__*/React.createElement("span", {
    style: {
      fontSize: "14px",
      paddingBottom: "4px"
    },
    className: activeTab === "finance-form" ? "active" : ""
  }, tShort("dhamen/financing_info"))), /*#__PURE__*/React.createElement("form", {
    onSubmit: handleSubmit(activeTab === "user-info-form" ? function (data) {
      // Prevent default form submission and switch to next tab
      handleNextTab();
      actions.setActiveTab("finance-form");
    } : handleFinanceFormSubmit),
    className: "pt-6 md:px-6 pb-6"
  }, activeTab === "user-info-form" && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("h1", {
    className: "font-bold py-1 text-[16px] md:text-lg text-[#484848]"
  }, " ", tShort("dhamen/enter_your_info")), /*#__PURE__*/React.createElement(Controller, {
    name: "first_name",
    control: control,
    render: function render(_ref3) {
      var _errors$first_name, _errors$first_name$me, _errors$first_name2;
      var field = _ref3.field;
      return /*#__PURE__*/React.createElement(Input, {
        className: "my-8",
        name: field.name,
        label: tShort("dhamen/first_name"),
        type: "text",
        error: (_errors$first_name = errors.first_name) !== null && _errors$first_name !== void 0 && (_errors$first_name$me = _errors$first_name.message) !== null && _errors$first_name$me !== void 0 && _errors$first_name$me.startsWith("dhamen/") ? tShort(errors.first_name.message) : ((_errors$first_name2 = errors.first_name) === null || _errors$first_name2 === void 0 ? void 0 : _errors$first_name2.message) || "",
        onChange: field.onChange,
        onBlur: field.onBlur,
        value: field.value
      });
    }
  }), /*#__PURE__*/React.createElement(Controller, {
    name: "last_name",
    control: control,
    render: function render(_ref4) {
      var _errors$last_name, _errors$last_name$mes, _errors$last_name2;
      var field = _ref4.field;
      return /*#__PURE__*/React.createElement(Input, {
        className: "my-8",
        name: field.name,
        label: tShort("dhamen/last_name"),
        type: "text",
        error: (_errors$last_name = errors.last_name) !== null && _errors$last_name !== void 0 && (_errors$last_name$mes = _errors$last_name.message) !== null && _errors$last_name$mes !== void 0 && _errors$last_name$mes.startsWith("dhamen/") ? tShort(errors.last_name.message) : ((_errors$last_name2 = errors.last_name) === null || _errors$last_name2 === void 0 ? void 0 : _errors$last_name2.message) || "",
        onChange: field.onChange,
        onBlur: field.onBlur,
        value: field.value
      });
    }
  }), /*#__PURE__*/React.createElement(Controller, {
    name: "id_number",
    control: control,
    render: function render(_ref5) {
      var _errors$id_number, _errors$id_number$mes, _errors$id_number2;
      var field = _ref5.field;
      return /*#__PURE__*/React.createElement(Input, {
        className: "my-8",
        maxLength: 10,
        name: field.name,
        label: tShort("dhamen/id_number"),
        type: "text",
        inputMode: "numeric",
        onlyNumbers: true,
        error: (_errors$id_number = errors.id_number) !== null && _errors$id_number !== void 0 && (_errors$id_number$mes = _errors$id_number.message) !== null && _errors$id_number$mes !== void 0 && _errors$id_number$mes.startsWith("dhamen/") ? tShort(errors.id_number.message) : ((_errors$id_number2 = errors.id_number) === null || _errors$id_number2 === void 0 ? void 0 : _errors$id_number2.message) || "",
        onChange: field.onChange,
        onBlur: field.onBlur,
        value: field.value
      });
    }
  }), /*#__PURE__*/React.createElement("div", {
    className: "my-8"

    // className="mt-10 mb-8"
  }, /*#__PURE__*/React.createElement(Controller, {
    name: "birth_date",
    control: control,
    render: function render(_ref6) {
      var _errors$birth_date, _errors$birth_date$me, _errors$birth_date2;
      var field = _ref6.field;
      var birthDateType = watch("birth_date_type");
      return /*#__PURE__*/React.createElement(DateCalendarGregorian, {
        label: tShort("dhamen/birth_date"),
        value: parseBirthDate(field.value, birthDateType),
        errorMessage: (_errors$birth_date = errors.birth_date) !== null && _errors$birth_date !== void 0 && (_errors$birth_date$me = _errors$birth_date.message) !== null && _errors$birth_date$me !== void 0 && _errors$birth_date$me.startsWith("dhamen/") ? tShort(errors.birth_date.message) : ((_errors$birth_date2 = errors.birth_date) === null || _errors$birth_date2 === void 0 ? void 0 : _errors$birth_date2.message) || "",
        yearsFrom: -80,
        yearsTo: -16,
        onChange: function onChange(newValue) {
          console.log({
            newValue: newValue
          });
          if (newValue) {
            // newValue contains Gregorian date with 1-based month
            var gregorianDateStr = "".concat(newValue.year, "-").concat(String(newValue.month).padStart(2, "0"), "-").concat(String(newValue.day || 1).padStart(2, "0"));

            // Calculate the Hijri equivalent
            var gregorianDate = new Date(newValue.year, newValue.month - 1, newValue.day || 1);
            var hijriDate = gregorianToHijri(gregorianDate);
            var hijriDateStr = "";
            if (hijriDate) {
              // hijriDate.month is 0-based (after AdhanService fix)
              hijriDateStr = "".concat(hijriDate.year, "-").concat(String(hijriDate.month + 1).padStart(2, "0"), "-").concat(String(hijriDate.day).padStart(2, "0"));
            }

            // Update all three fields
            field.onChange(gregorianDateStr);
            setValue("birth_date_h", hijriDateStr);
            setValue("birth_date_type", (newValue === null || newValue === void 0 ? void 0 : newValue.type) || "G");
          }
        }
      });
    }
  })), /*#__PURE__*/React.createElement(Controller, {
    name: "mobile_number",
    control: control,
    render: function render(_ref7) {
      var _errors$mobile_number, _errors$mobile_number2, _errors$mobile_number3;
      var field = _ref7.field;
      // Create a wrapper to ensure proper integration with react-hook-form
      var handleChange = function handleChange(value) {
        field.onChange(value);
      };
      var handleBlur = function handleBlur() {
        field.onBlur();
      };
      return /*#__PURE__*/React.createElement(InputPhoneNumber, {
        className: "my-8 opacity-60 pointer-events-none",
        defaultValue: field.value,
        onChange: function onChange() {} // Prevent changes by providing empty function
        ,
        onBlur: handleBlur,
        onFocus: function onFocus() {},
        errorLabel: (_errors$mobile_number = errors.mobile_number) !== null && _errors$mobile_number !== void 0 && (_errors$mobile_number2 = _errors$mobile_number.message) !== null && _errors$mobile_number2 !== void 0 && _errors$mobile_number2.startsWith("dhamen/") ? tShort(errors.mobile_number.message) : ((_errors$mobile_number3 = errors.mobile_number) === null || _errors$mobile_number3 === void 0 ? void 0 : _errors$mobile_number3.message) || ""
      });
    }
  }), /*#__PURE__*/React.createElement(MobileDesktopButton, {
    onClick: function onClick() {
      actions.setActiveTab("finance-form");
      handleNextTab();
    },
    disabled: !isFirstTabValid() || isValidating,
    isSubmitting: isValidating,
    className: !isFirstTabValid() || isValidating ? "opacity-50 cursor-not-allowed" : ""
  }, isValidating ? tShort("dhamen/validating") : tShort("dhamen/next"))), activeTab === "finance-form" && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("h1", {
    className: "font-bold pt-1 pb-6 md:pb-1 text-[16px] md:text-lg text-[#484848]"
  }, " ", tShort("dhamen/enter_financial_info")), isMobile && /*#__PURE__*/React.createElement(OrderData, null), /*#__PURE__*/React.createElement(Controller, {
    name: "monthly_income",
    control: control,
    render: function render(_ref8) {
      var _errors$monthly_incom, _errors$monthly_incom2, _errors$monthly_incom3, _errors$monthly_incom4;
      var field = _ref8.field;
      var handleChange = function handleChange(value) {
        field.onChange(value.toString());
      };
      var handleBlur = function handleBlur() {
        field.onBlur();

        // Check if monthly income is below minimum after user finishes typing
        var currentValue = Number(field.value);
        if (currentValue > 0 && currentValue < MIN_MONTHLY_INCOME) {
          // Set validation error under the field
          setError("monthly_income", {
            type: "manual",
            message: tShort("dhamen/income_validation_error")
          });
        } else if (currentValue >= MIN_MONTHLY_INCOME) {
          // Clear error if validation passes
          setError("monthly_income", {});
        }
      };
      return /*#__PURE__*/React.createElement("div", {
        className: "my-8"
      }, /*#__PURE__*/React.createElement(InputCurrency, {
        inputClassName: "custom-input-class ".concat(errors.monthly_income ? "border-red-500" : ""),
        defaultValue: field.value ? Number(field.value) : undefined,
        currencyLabel: field.value ? tShort("dhamen/sar") : "",
        label: tShort("dhamen/monthly_income_total"),
        lang: "en",
        errorLabel: (_errors$monthly_incom = errors.monthly_income) !== null && _errors$monthly_incom !== void 0 && (_errors$monthly_incom2 = _errors$monthly_incom.message) !== null && _errors$monthly_incom2 !== void 0 && _errors$monthly_incom2.startsWith("dhamen/") ? tShort(errors.monthly_income.message) : ((_errors$monthly_incom3 = errors.monthly_income) === null || _errors$monthly_incom3 === void 0 ? void 0 : _errors$monthly_incom3.message) || "",
        onChange: handleChange,
        onBlur: handleBlur,
        onFocus: function onFocus() {}
      }), errors.monthly_income && /*#__PURE__*/React.createElement("span", {
        className: "text-red-500 text-sm mt-1 block"
      }, (_errors$monthly_incom4 = errors.monthly_income.message) !== null && _errors$monthly_incom4 !== void 0 && _errors$monthly_incom4.startsWith("dhamen/") ? tShort(errors.monthly_income.message) : errors.monthly_income.message));
    }
  }), /*#__PURE__*/React.createElement(Controller, {
    name: "monthly_obligations",
    control: control,
    render: function render(_ref9) {
      var _errors$monthly_oblig, _errors$monthly_oblig2, _errors$monthly_oblig3, _errors$monthly_oblig4;
      var field = _ref9.field;
      var handleChange = function handleChange(value) {
        field.onChange(value.toString());
      };
      var handleBlur = function handleBlur() {
        field.onBlur();
      };
      return /*#__PURE__*/React.createElement("div", {
        className: "my-8"
      }, /*#__PURE__*/React.createElement(InputCurrency, {
        inputClassName: "custom-input-class ".concat(errors.monthly_obligations ? "border-red-500" : ""),
        defaultValue: field.value ? Number(field.value) : undefined,
        currencyLabel: field.value ? tShort("dhamen/sar") : "",
        label: tShort("dhamen/monthly_obligations"),
        lang: "en",
        errorLabel: (_errors$monthly_oblig = errors.monthly_obligations) !== null && _errors$monthly_oblig !== void 0 && (_errors$monthly_oblig2 = _errors$monthly_oblig.message) !== null && _errors$monthly_oblig2 !== void 0 && _errors$monthly_oblig2.startsWith("dhamen/") ? tShort(errors.monthly_obligations.message) : ((_errors$monthly_oblig3 = errors.monthly_obligations) === null || _errors$monthly_oblig3 === void 0 ? void 0 : _errors$monthly_oblig3.message) || "",
        onChange: handleChange,
        onBlur: handleBlur,
        onFocus: function onFocus() {}
      }), errors.monthly_obligations && /*#__PURE__*/React.createElement("span", {
        className: "text-red-500 text-sm mt-1 block"
      }, (_errors$monthly_oblig4 = errors.monthly_obligations.message) !== null && _errors$monthly_oblig4 !== void 0 && _errors$monthly_oblig4.startsWith("dhamen/") ? tShort(errors.monthly_obligations.message) : errors.monthly_obligations.message));
    }
  }), /*#__PURE__*/React.createElement(Controller, {
    name: "financing_duration",
    control: control,
    render: function render(_ref0) {
      var _errors$financing_dur;
      var field = _ref0.field;
      return /*#__PURE__*/React.createElement("div", {
        className: "my-8"
      }, /*#__PURE__*/React.createElement("label", {
        className: "block text-sm font-bold text-gray-700 mb-3"
      }, tShort("dhamen/financing_duration_years")), /*#__PURE__*/React.createElement("div", {
        className: "flex gap-4 justify-center w-full"
      }, [1, 2, 3, 4, 5].map(function (year) {
        return /*#__PURE__*/React.createElement("div", {
          key: year,
          onClick: function onClick() {
            return field.onChange(year.toString());
          },
          className: "\n                    flex flex-col items-center justify-center\n                    w-1/5 h-12 border-2  rounded-[4px] cursor-pointer\n                    transition-all duration-200 hover:shadow-sm\n                    ".concat(field.value === year.toString() ? "border-[#154f9c]  bg-[#154f9c]  text-white" : "border-[1px] border-solid   rounded-[4px] border-[#D6DFE9] -outline-offset-1  bg-white text-[#6C7A8D] hover:border-gray-400", "\n                  ")
        }, /*#__PURE__*/React.createElement("span", {
          className: "text-[16px] font-bold"
        }, year), /*#__PURE__*/React.createElement("span", {
          className: "text-[10px]"
        }, tShort("dhamen/years")));
      })), errors.financing_duration && /*#__PURE__*/React.createElement("span", {
        className: "text-red-500 text-sm mt-1 block text-center"
      }, (_errors$financing_dur = errors.financing_duration.message) !== null && _errors$financing_dur !== void 0 && _errors$financing_dur.startsWith("dhamen/") ? tShort(errors.financing_duration.message) : errors.financing_duration.message));
    }
  }), /*#__PURE__*/React.createElement(Controller, {
    name: "has_down_payment",
    control: control,
    render: function render(_ref1) {
      var field = _ref1.field;
      return /*#__PURE__*/React.createElement("div", {
        className: "mt-4"
      }, /*#__PURE__*/React.createElement("label", {
        className: "flex items-center cursor-pointer"
      }, /*#__PURE__*/React.createElement("input", {
        type: "checkbox",
        checked: field.value || false,
        onChange: function onChange(e) {
          field.onChange(e.target.checked);
          if (!e.target.checked) {
            setValue("down_payment", "");
            clearErrors("down_payment");
          }
        },
        className: "ml-2 w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
      }), /*#__PURE__*/React.createElement("span", {
        className: "text-sm font-medium text-gray-700 px-1"
      }, tShort("dhamen/want_down_payment"))));
    }
  }), /*#__PURE__*/React.createElement(Controller, {
    name: "down_payment",
    control: control,
    render: function render(_ref10) {
      var _errors$down_payment, _errors$down_payment$, _errors$down_payment2, _errors$down_payment$2;
      var field = _ref10.field;
      var isEnabled = watch("has_down_payment");
      var handleChange = function handleChange(value) {
        if (isEnabled) {
          var valueStr = value.toString();
          field.onChange(valueStr);

          // Check if financing amount would be below minimum
          var downPaymentAmount = parseFloat(valueStr) || 0;
          var calculatedFinancingAmount = actions.calculateFinancingAmount(downPaymentAmount);
          // Validate down payment amount
          var validationError = validateDownPayment(valueStr);
          if (validationError) {
            setError("down_payment", {
              type: "manual",
              message: validationError
            });
          } else {
            // Clear error if validation passes
            clearErrors("down_payment");
          }
        }
      };
      var handleBlur = function handleBlur() {
        field.onBlur();
      };
      return /*#__PURE__*/React.createElement("div", {
        className: "mb-8 mt-6"
      }, /*#__PURE__*/React.createElement(InputCurrency, {
        key: "down_payment_".concat(isEnabled),
        inputClassName: "custom-input-class ".concat(!isEnabled ? "opacity-50 cursor-not-allowed pointer-events-none" : "", " ").concat(errors.down_payment ? "border-red-500" : ""),
        defaultValue: field.value && isEnabled ? Number(field.value) : undefined,
        currencyLabel: field.value && isEnabled ? tShort("dhamen/sar") : "",
        label: tShort("dhamen/down_payment_amount"),
        lang: "en",
        errorLabel: (_errors$down_payment = errors.down_payment) !== null && _errors$down_payment !== void 0 && (_errors$down_payment$ = _errors$down_payment.message) !== null && _errors$down_payment$ !== void 0 && _errors$down_payment$.startsWith("dhamen/") ? tShort(errors.down_payment.message) : ((_errors$down_payment2 = errors.down_payment) === null || _errors$down_payment2 === void 0 ? void 0 : _errors$down_payment2.message) || "",
        onChange: isEnabled ? handleChange : function () {}
      }), errors.down_payment && isEnabled && /*#__PURE__*/React.createElement("span", {
        className: "text-red-500 text-sm mt-1 block"
      }, (_errors$down_payment$2 = errors.down_payment.message) !== null && _errors$down_payment$2 !== void 0 && _errors$down_payment$2.startsWith("dhamen/") ? tShort(errors.down_payment.message) : errors.down_payment.message));
    }
  }), /*#__PURE__*/React.createElement(Controller, {
    name: "consent",
    control: control,
    render: function render(_ref11) {
      var field = _ref11.field;
      return /*#__PURE__*/React.createElement("div", {
        className: "mb-6"
      }, /*#__PURE__*/React.createElement("label", {
        className: "flex items-start"
      }, /*#__PURE__*/React.createElement("input", {
        type: "checkbox",
        checked: field.value || false,
        onChange: function onChange(e) {
          var isChecked = e.target.checked;
          field.onChange(isChecked);
          actions.updateConsent(isChecked);
        },
        className: "ml-2 w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 flex-shrink-0 cursor-pointer"
      }), /*#__PURE__*/React.createElement("span", {
        className: "text-sm font-medium text-gray-700 px-1"
      }, /*#__PURE__*/React.createElement("span", {
        className: "cursor-pointer hover:text-[#0d3a6e] font-bold"
      }, tShort("dhamen/consent"))), /*#__PURE__*/React.createElement("span", {
        onClick: function onClick() {
          return setShowTermsModal(true);
        },
        className: "text-[#154f9c] underline cursor-pointer hover:text-[#0d3a6e] underLine font-bold"
      }, tShort("dhamen/consent2"))));
    }
  }), /*#__PURE__*/React.createElement(MobileDesktopButton, {
    onClick: function onClick() {
      return handleSubmit(handleFinanceFormSubmit)();
    },
    disabled: !isValid || isValidating || !!errors.down_payment || !!errors.monthly_obligations || !consentValue,
    isSubmitting: isValidating,
    className: !isValid || isValidating || !!errors.down_payment || !!errors.monthly_obligations || !consentValue ? "opacity-50 cursor-not-allowed" : ""
  }, isValidating ? tShort("dhamen/submitting") : tShort("dhamen/continue_request")))), /*#__PURE__*/React.createElement(InfoModal, {
    open: showTermsModal,
    onClose: function onClose() {
      return setShowTermsModal(false);
    },
    toggle: function toggle() {
      return setShowTermsModal(!showTermsModal);
    },
    title: tShort("dhamen/terms_and_conditions_title"),
    text: tShort("dhamen/terms_and_conditions_text"),
    titleClassNames: "text-xl font-bold text-[#154f9c] mb-4 text-start"
  }));
}
export default DhamenInfoForm;